import { View, Button } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { useState } from "react";
import { DotsThreeOutline } from "@/components/shared/icons";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { DropdownMenuGroup } from "@radix-ui/react-dropdown-menu";
import { useUpdateContactMutation } from "@/gqlhooks/profile/useUpdateContactMutation";
import { useDeleteContactMutation } from "@/gqlhooks/profile/useDeleteContactMutation";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";
import { useClientContactsQuery } from "@/gqlhooks/profile/useClientContactsQuery";

export const ContactMoreActions = ({ contactId }: { contactId: string }) => {
  const { data: contacts } = useClientContactsQuery();
  const { isMobile } = useWindowDimensions();
  const [openMoreActions, setOpenMoreActions] = useState<boolean>(false);

  const [updateContactMutation, { loading: updateLoading }] =
    useUpdateContactMutation();

  const [deleteContactMutation, { loading: deleteLoading }] =
    useDeleteContactMutation();

  const loading = updateLoading || deleteLoading;

  const handleSetPrimaryContact = async () => {
    const existingContacts = contacts || [];

    const updatedPrimaryContact = existingContacts.find(
      (cont) => cont.id === contactId,
    );

    try {
      if (updatedPrimaryContact?.id) {
        const result = await updateContactMutation({
          variables: {
            input: {
              id: updatedPrimaryContact.id,
              firstName: updatedPrimaryContact.firstName,
              lastName: updatedPrimaryContact.lastName,
              email: updatedPrimaryContact.email,
              phoneNumber: updatedPrimaryContact.phoneNumber,
              primary: true,
            },
          },
        });

        if (result.data?.updateContact?.contact) {
          const lastPrimaryContact = existingContacts.filter(
            (cont) => cont.primary && cont.id !== contactId,
          );

          if (lastPrimaryContact.length > 0) {
            await Promise.all(
              lastPrimaryContact.map((contact) =>
                updateContactMutation({
                  variables: {
                    input: {
                      id: contact.id,
                      firstName: contact.firstName,
                      lastName: contact.lastName,
                      email: contact.email,
                      phoneNumber: contact.phoneNumber,
                      primary: false,
                    },
                  },
                }),
              ),
            );
          }

          showToast({
            type: "success",
            title: "Success update contact",
          });
        }
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to update contact");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const handleClose = () => {
    setOpenMoreActions(false);
  };

  const handleDeleteContact = async () => {
    try {
      const result = await deleteContactMutation({
        variables: {
          input: {
            id: contactId,
          },
        },
      });

      if (result.data?.deleteContact?.result && !loading) {
        handleClose();
        showToast({
          title: "Success delete contact",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to delete contact");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  return (
    <>
      <DropdownMenu onOpenChange={setOpenMoreActions}>
        <DropdownMenuTrigger>
          <View className="flex items-center rounded-full border border-primary-border p-2">
            <DotsThreeOutline
              weight="duotone"
              className="size-5 text-primary"
            />
          </View>
        </DropdownMenuTrigger>
        {!isMobile && (
          <DropdownMenuContent className="-left-10 p-2 px-3">
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Button
                  variant="tertiary"
                  color="foreground"
                  loading={loading}
                  className="items-start gap-3 rounded-none border-b border-secondary-border p-0 pb-2"
                  onClick={handleSetPrimaryContact}
                  iconName="addressBook"
                  children="Set as default Contact"
                />
              </DropdownMenuItem>

              <DropdownMenuItem>
                <Button
                  variant="secondary"
                  color="danger"
                  loading={loading}
                  className="items-start gap-3 border-0 p-0"
                  onClick={handleDeleteContact}
                  iconName="trash"
                  iconColor="danger"
                  children="Delete Contact"
                />
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      {isMobile && (
        <BottomDrawerModal
          open={openMoreActions}
          onOpenChange={setOpenMoreActions}
        >
          <View className="flex flex-col gap-4">
            <Button
              variant="tertiary"
              color="foreground"
              fullWidth="full"
              loading={loading}
              className="justify-start gap-4 rounded-none border-b border-secondary-border p-0 pb-5"
              onClick={handleSetPrimaryContact}
              iconName="addressBook"
              children="Make Default Contact"
            />

            <Button
              variant="secondary"
              color="danger"
              loading={loading}
              fullWidth="full"
              className="justify-start gap-4 border-0 p-0"
              onClick={handleDeleteContact}
              iconName="trash"
              iconColor="danger"
              children="Delete Contact"
            />

            <Button
              variant="primary"
              color="CTA"
              loading={loading}
              onClick={handleClose}
              children="Close"
            />
          </View>
        </BottomDrawerModal>
      )}
    </>
  );
};
